import React from "react";
import { motion } from "framer-motion";
import { ReactComponent as IndiaFeatures } from "../pages/in.svg";
import "./IndiaMap.css";

const HIGHLIGHT_IDS = ["INTG", "INAP"];

// State centroids lifted from the source map's label_points layer (same 1000x1000 viewBox).
const STATE_POINTS = [
  { id: "INAN", name: "Andaman & Nicobar", x: 770.3, y: 814.6 },
  { id: "INTG", name: "Telangana", x: 396.7, y: 641.9 },
  { id: "INAP", name: "Andhra Pradesh", x: 392.3, y: 730.7 },
  { id: "INAR", name: "Arunachal Pradesh", x: 826.3, y: 325.9 },
  { id: "INAS", name: "Assam", x: 775.9, y: 393.9 },
  { id: "INBR", name: "Bihar", x: 575.8, y: 411 },
  { id: "INCH", name: "Chandigarh", x: 334.9, y: 255.1 },
  { id: "INCT", name: "Chhattisgarh", x: 473.5, y: 533.4 },
  { id: "INDH", name: "Dadra & Nagar Haveli", x: 232.5, y: 573.8 },
  { id: "INDL", name: "Delhi", x: 344.1, y: 320 },
  { id: "INGA", name: "Goa", x: 261.6, y: 713.9 },
  { id: "INGJ", name: "Gujarat", x: 199.2, y: 481 },
  { id: "INHR", name: "Haryana", x: 323, y: 305.2 },
  { id: "INHP", name: "Himachal Pradesh", x: 348.4, y: 217.7 },
  { id: "INJH", name: "Jharkhand", x: 563.9, y: 475.8 },
  { id: "INKA", name: "Karnataka", x: 302.1, y: 728.1 },
  { id: "INKL", name: "Kerala", x: 325.5, y: 848.9 },
  { id: "INMP", name: "Madhya Pradesh", x: 378.4, y: 494.2 },
  { id: "INMH", name: "Maharashtra", x: 305.7, y: 597.9 },
  { id: "INMN", name: "Manipur", x: 801.5, y: 443.1 },
  { id: "INML", name: "Meghalaya", x: 737, y: 417.6 },
  { id: "INMZ", name: "Mizoram", x: 776.2, y: 476.2 },
  { id: "INNL", name: "Nagaland", x: 822.1, y: 399 },
  { id: "INOR", name: "Odisha", x: 549.9, y: 559.3 },
  { id: "INPY", name: "Puducherry", x: 417.7, y: 838.9 },
  { id: "INPB", name: "Punjab", x: 300.5, y: 254.4 },
  { id: "INRJ", name: "Rajasthan", x: 256.7, y: 375.8 },
  { id: "INSK", name: "Sikkim", x: 654.3, y: 355.6 },
  { id: "INTN", name: "Tamil Nadu", x: 379.6, y: 835.6 },
  { id: "INTR", name: "Tripura", x: 740.6, y: 472.2 },
  { id: "INUP", name: "Uttar Pradesh", x: 438.7, y: 375.5 },
  { id: "INUT", name: "Uttarakhand", x: 401.4, y: 272.1 },
  { id: "INWB", name: "West Bengal", x: 637.1, y: 485.8 },
  { id: "INLD", name: "Lakshadweep", x: 233, y: 912.6 },
  { id: "INJK", name: "Jammu & Kashmir", x: 274.8, y: 156.1 },
  { id: "INLA", name: "Ladakh", x: 344.9, y: 136.5 },
];

const ActiveState = ({ x, y, name, delay = 0 }) => (
  <g>
    <motion.circle
      cx={x}
      cy={y}
      r={34}
      fill="#FF6B35"
      animate={{ scale: [1, 1.8, 1], opacity: [0.4, 0, 0.4] }}
      transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay }}
    />
    <motion.circle
      initial={{ scale: 0 }}
      whileInView={{ scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay, type: "spring", stiffness: 260, damping: 16 }}
      cx={x}
      cy={y}
      r={16}
      fill="#FF6B35"
      stroke="#fff"
      strokeWidth="3"
    />
    <text x={x + 24} y={y + 8} className="fill-white font-display font-bold" style={{ fontSize: 28 }}>
      {name}
    </text>
  </g>
);

const PlannedState = ({ x, y, delay = 0 }) => (
  <motion.circle
    initial={{ opacity: 0, scale: 0 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    transition={{ delay, duration: 0.4 }}
    cx={x}
    cy={y}
    r={9}
    fill="#ffffff"
    fillOpacity={0.4}
  />
);

export const IndiaMap = () => (
  <div>
    <div className="relative aspect-square w-full max-w-md mx-auto">
      <div className="india-map-svg absolute inset-0">
        <IndiaFeatures role="img" aria-label="Map of India" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" />
      </div>
      <svg viewBox="0 0 1000 1000" className="absolute inset-0 h-full w-full pointer-events-none">
        {STATE_POINTS.filter((s) => !HIGHLIGHT_IDS.includes(s.id)).map((s, i) => (
          <PlannedState key={s.id} x={s.x} y={s.y} delay={i * 0.02} />
        ))}
        {STATE_POINTS.filter((s) => HIGHLIGHT_IDS.includes(s.id)).map((s, i) => (
          <ActiveState key={s.id} x={s.x} y={s.y} name={s.name} delay={0.6 + i * 0.25} />
        ))}
      </svg>
    </div>
    <div className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs font-mono uppercase tracking-wider">
      <span className="inline-flex items-center gap-2 text-teal-100">
        <span className="h-2.5 w-2.5 rounded-full bg-coral" /> Fully active today
      </span>
      <span className="inline-flex items-center gap-2 text-teal-100/70">
        <span className="h-2.5 w-2.5 rounded-full bg-white/40" /> Expanding here next
      </span>
    </div>
  </div>
);
